import CodeBlock from "../../common/code_block";
import ErrorCodesLink from "../../common/ErrorCodesLink";
import HeaderWithTag from "../../common/header_with_tag";
import { AccountEntity, IValidateAccountRequest } from "../../types/types";
import { HOST_API_URL } from "../../config";
import TryValidateAccount from "./try";

const requestCode: IValidateAccountRequest = {
  account: "201018078601",
  entity: AccountEntity.DBI,
  type: "bank",
};

const responseCode = `{
  "statusCode": 200,
  "message": "Success",
  "errorCode": null,
  "data": {
    "accountId": "201018078601",
    "name": "Abdirazak Ibrahim Abdirahman",
    "agentId": "DBF",
    "subAgentId": null,
    "address": "123 Main St, Nairobi",
    "dob": null,
    "msisdn": "252654249184",
    "currency": "USD",
    "accountType": "BANK",
    "mandates": null,
    "operationMode": "DBI"
  }
}`;

const failureResponseCode = `{
  "statusCode": 400,
  "message": "account validation failed",
  "errorCode": "DPI-2003",
  "data": null
}`;

const accountNotFoundResponseCode = `{
  "statusCode": 400,
  "message": "Account not found",
  "errorCode": "DPI-2007",
  "data": null
}`;

const walletHandlingTree = `Call Validate Account
├─ 200           → wallet confirmed, proceed with Send Remittance
├─ DPI-2007      → stop, report as Account Not Found, correct at source
├─ DPI-2004      → stop, account number missing from the request
├─ DPI-2002      → check the number format, then retry 2-3 times with a short delay
│                  still failing → stop, contact support with X-Request-Id
└─ any other 400 → stop, do not send`;

const walletCodes = [
  {
    code: "DPI-2007",
    message: "Account not found",
    meaning: "The wallet does not exist",
    action: "Do not retry. The account is invalid; correct it at source.",
  },
  {
    code: "DPI-2002",
    message: "mobile validation failed",
    meaning: "The wallet could not be confirmed",
    action:
      "Check the number format, then retry two or three times with a short delay. If it persists, stop and contact support quoting the X-Request-Id response header.",
  },
];

const walletSandboxCases = [
  { account: "252654249184", result: "200 Success — Abdirazak Ibrahim Abdirahman" },
  {
    account: "0654249184, 654249184, 00252654249184, +252 654 249 184",
    result: "200 Success — same wallet",
  },
  { account: "252700000000", result: "400 DPI-2007 — Account not found" },
  { account: "252654249185", result: "400 DPI-2007 — Account not found" },
  { account: "12345", result: "400 DPI-2002 — mobile validation failed" },
  { account: "(omitted or empty)", result: "400 DPI-2004 — MobileNo is required" },
];

function ValidateAccount() {
  return (
    <div className="page">
      <div className="app-content main-content mt-0">
        <div className="side-app">
          <div className="main-container container-fluid">
            <div className="page-header">
              <div>
                <h1 className="page-title">Validate Account</h1>
              </div>
              <div className="ms-auto pageheader-btn">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="javascript:void(0);">Docs</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Validate Account
                  </li>
                </ol>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="row">
                <div className="col-md-7" style={{ position: "relative" }}>
                  <div className="card-body border-end p-0">
                    <div className="p-5">
                      <HeaderWithTag
                        headerText="Validate an account"
                        headerTag="description"
                      />
                      <p className="text-12">
                        <span className="badge badge-sm bg-blue text-white me-2">
                          POST
                        </span>
                        {HOST_API_URL}/Remittances/ValidateAccount
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        Confirms whether an account or wallet exists and returns the holder's
                        details. Validate the destination before every transfer; do not bypass
                        this check.
                      </p>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Request"
                          headerTag="request"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                                <th>Min</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Account</td>
                                <td>string</td>
                                <td>
                                  Account number or wallet number. An EDAHAB wallet may be
                                  sent with or without the country code; non-digit
                                  characters are ignored.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Entity</td>
                                <td>string</td>
                                <td>
                                  Entity name as listed in the Appendix (EDAHAB, MPESA,
                                  DBI, PESALink, …). Which entities are valid depends on
                                  Type; see the note below.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Type</td>
                                <td>string</td>
                                <td>
                                  wallet or bank (case-insensitive). Any other value
                                  returns DPI-2001.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Code</td>
                                <td>string</td>
                                <td>
                                  Destination bank or network code. Used only for PESALink
                                  and ABC lookups; ignored for other entities.
                                </td>
                                <td>No</td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="alert alert-info text-14 mb-0">
                          <strong>Entity and Type go together.</strong> With{" "}
                          <code>type: "wallet"</code> the valid entities are EDAHAB, MPESA,
                          TANZANIA, UGANDA, BANGLADESH and PESALink. With{" "}
                          <code>type: "bank"</code> they are DMT, DBI, EAB, PESALink and
                          ABC. An entity that is not valid for the given Type fails
                          validation (DPI-2002 for wallet, DPI-2003 for bank). A name that
                          is not in the list is rejected during model binding with
                          DPI-1006.
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={JSON.stringify(requestCode, null, 2)}
                          codeBlackText="Request"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Response"
                          headerTag="response"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameters</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>statusCode</td>
                                <td>integer</td>
                                <td>200 on success, otherwise the failure status</td>
                              </tr>
                              <tr>
                                <td>message</td>
                                <td>string</td>
                                <td>"Success", or the reason for the failure</td>
                              </tr>
                              <tr>
                                <td>errorCode</td>
                                <td>string</td>
                                <td>null on success; a stable DPI-xxxx code on failure</td>
                              </tr>
                              <tr>
                                <td>data.accountId</td>
                                <td>string</td>
                                <td>The validated account or wallet number</td>
                              </tr>
                              <tr>
                                <td>data.name</td>
                                <td>string</td>
                                <td>Account holder's name</td>
                              </tr>
                              <tr>
                                <td>data.agentId</td>
                                <td>string</td>
                                <td>Agent identifier for the destination</td>
                              </tr>
                              <tr>
                                <td>data.subAgentId</td>
                                <td>string</td>
                                <td>Sub-agent identifier, where applicable</td>
                              </tr>
                              <tr>
                                <td>data.address</td>
                                <td>string</td>
                                <td>Account holder's address, where available</td>
                              </tr>
                              <tr>
                                <td>data.dob</td>
                                <td>string</td>
                                <td>Account holder's date of birth, where available</td>
                              </tr>
                              <tr>
                                <td>data.msisdn</td>
                                <td>string</td>
                                <td>Mobile number linked to the account, where available</td>
                              </tr>
                              <tr>
                                <td>data.currency</td>
                                <td>string</td>
                                <td>Currency of the validated account</td>
                              </tr>
                              <tr>
                                <td>data.accountType</td>
                                <td>string</td>
                                <td>WALLET or BANK, derived from the requested Type</td>
                              </tr>
                              <tr>
                                <td>data.operationMode</td>
                                <td>string</td>
                                <td>The validated entity, as its canonical name (EDAHAB, PESALink, …)</td>
                              </tr>
                              <tr>
                                <td>data.mandates</td>
                                <td>object</td>
                                <td>Reserved for future use; currently null</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={responseCode}
                          codeBlackText="Response Simple"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={failureResponseCode}
                          codeBlackText="Failure Example"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Wallet validation outcomes"
                          headerTag="walletOutcomes"
                        />
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          Applies to Edahab wallet validation (<code>entity: "EDAHAB"</code>,{" "}
                          <code>type: "wallet"</code>). Both codes below are returned with
                          HTTP status 400, and the transfer must not proceed on either of
                          them.
                        </p>

                        <h6 className="mt-4 mb-2">Why DPI-2007 exists</h6>
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          Previously a single code, DPI-2002, was returned both when a wallet
                          did not exist and when the wallet could not be checked. Those two
                          situations call for opposite responses &mdash; one is permanent,
                          the other may be temporary &mdash; and partners had no way to tell
                          them apart. DPI-2007 now identifies the permanent case. DPI-2002 is
                          retained for every case where the wallet could not be confirmed.
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th className="text-nowrap">Code</th>
                                <th className="text-nowrap">Message</th>
                                <th className="text-nowrap">Meaning</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {walletCodes.map((row) => (
                                <tr key={row.code}>
                                  <td className="text-nowrap">
                                    <strong>{row.code}</strong>
                                  </td>
                                  <td className="text-nowrap">{row.message}</td>
                                  <td className="text-nowrap">{row.meaning}</td>
                                  <td>{row.action}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="my-4">
                          <CodeBlock
                            code={accountNotFoundResponseCode}
                            codeBlackText="Account Not Found Example"
                            codeLanguage="json"
                          />
                        </div>

                        <h6 className="mt-4 mb-2">When each code is returned</h6>
                        <p className="text-14 mb-1">
                          <strong>DPI-2007 &mdash; Account not found</strong>
                        </p>
                        <ul className="text-muted text-14">
                          <li>The wallet provider confirms the number is not registered</li>
                          <li>The wallet provider returns a record with no account holder</li>
                        </ul>
                        <p className="text-14 mb-1">
                          <strong>DPI-2002 &mdash; Could not be confirmed</strong>
                        </p>
                        <ul className="text-muted text-14">
                          <li>The account number is not a valid wallet number format</li>
                          <li>
                            The wallet lookup service is unreachable, times out, or returns
                            an unexpected response
                          </li>
                          <li>The daily validation allowance has been reached</li>
                        </ul>

                        <h6 className="mt-4 mb-2">Scope</h6>
                        <p className="text-muted text-14 mb-1">
                          DPI-2007 applies only to Edahab wallet validation. No other lookup
                          is affected:
                        </p>
                        <ul className="text-muted text-14">
                          <li>
                            Bank account validation (<code>type: "bank"</code>) continues to
                            return DPI-2003 (account validation failed)
                          </li>
                          <li>
                            All other wallet entities (MPESA, TANZANIA, UGANDA, BANGLADESH,
                            PESALink) continue to return DPI-2002 when validation fails
                          </li>
                          <li>Send Remittance is unchanged</li>
                        </ul>

                        <div className="alert alert-warning text-14">
                          <strong>Change of behaviour.</strong> One case has changed from a
                          success to a failure. Where the wallet provider returned a record
                          with no account holder name, Validate Account previously returned
                          HTTP 200 with an empty <code>name</code>. It now returns 400
                          DPI-2007. If your integration treats <code>statusCode: 200</code>{" "}
                          as confirmation that a wallet exists, you may now receive a 400
                          for account numbers that previously appeared to validate. This is
                          intended: those accounts were never valid, and sending to them
                          would have failed at payout.
                        </div>

                        <h6 className="mt-4 mb-2">Sandbox testing</h6>
                        <p className="text-muted text-14">
                          With <code>entity: "EDAHAB"</code> and <code>type: "wallet"</code>:
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>account</th>
                                <th>Response</th>
                              </tr>
                            </thead>
                            <tbody>
                              {walletSandboxCases.map((row) => (
                                <tr key={row.account}>
                                  <td>
                                    <code>{row.account}</code>
                                  </td>
                                  <td className="text-nowrap">{row.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <h6 className="mt-4 mb-2">Recommended handling</h6>
                        <pre className="language-js p-3 border border-1">{walletHandlingTree}</pre>
                        <p className="text-muted text-14 mb-0">
                          Every response from the API carries an <code>X-Request-Id</code>{" "}
                          header. Quote it when raising an issue and the exact request can
                          be traced.
                        </p>
                      </div>

                      <ErrorCodesLink />
                    </div>
                  </div>
                </div>
                <div className="col-md-5 pt-5">
                  <h5 className="card-title text-primary">
                    Code Samples
                  </h5>

                  <TryValidateAccount
                    codeBlackTitle="Request Simple"
                    validateAccountObject={requestCode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ValidateAccount;